import { ChangeEvent, ClipboardEvent, useCallback, useEffect, useRef } from 'react'
import type { Editor } from '@tiptap/react'
import type { TaskTrackerPendingAttachmentFile } from './TaskTrackerRichTextEditor.shared'
import { getTaskDescriptionFileSizeValidationError } from '../../lib/taskTrackerDescription.utils'

type UseTaskTrackerRichTextAttachmentsParams = {
  editor: Editor | null
  disabled: boolean
  allowAttachments?: boolean
  onFileReject?: (message: string) => void
}

export const useTaskTrackerRichTextAttachments = ({
  editor,
  disabled,
  allowAttachments = true,
  onFileReject,
}: UseTaskTrackerRichTextAttachmentsParams) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const attachmentInputRef = useRef<HTMLInputElement>(null)
  const blobUrlsRef = useRef<Set<string>>(new Set())
  const pendingAttachmentFilesRef = useRef<
    Map<string, { file: File; fileName: string }>
  >(new Map())

  useEffect(() => {
    return () => {
      blobUrlsRef.current.forEach((url) => URL.revokeObjectURL(url))
      blobUrlsRef.current.clear()
    }
  }, [])

  const rejectFileIfOversized = useCallback(
    (file: File) => {
      const validationError = getTaskDescriptionFileSizeValidationError(file)
      if (!validationError) return false

      onFileReject?.(validationError)
      return true
    },
    [onFileReject],
  )

  const insertImage = useCallback(
    (src: string, name = 'Изображение') => {
      if (!editor || !src) return

      editor
        .chain()
        .focus()
        .insertContent([
          {
            type: 'image',
            attrs: {
              src,
              alt: name,
              title: name,
              width: '100%',
            },
          },
          { type: 'paragraph' },
        ])
        .run()
    },
    [editor],
  )

  const insertLocalImage = useCallback(
    (file: File) => {
      if (!editor || !file?.size || disabled || !allowAttachments) return
      if (rejectFileIfOversized(file)) return

      const objectUrl = URL.createObjectURL(file)
      blobUrlsRef.current.add(objectUrl)
      insertImage(objectUrl, file.name || 'Изображение')
    },
    [allowAttachments, disabled, editor, insertImage, rejectFileIfOversized],
  )

  const insertLocalFile = useCallback(
    (file: File) => {
      if (!editor || !file?.size || disabled || !allowAttachments) return
      if (rejectFileIfOversized(file)) return

      const objectUrl = URL.createObjectURL(file)
      blobUrlsRef.current.add(objectUrl)
      const fileName = (file.name || 'Файл').trim() || 'Файл'
      pendingAttachmentFilesRef.current.set(objectUrl, { file, fileName })

      editor
        .chain()
        .focus()
        .insertContent([
          {
            type: 'paragraph',
            content: [
              {
                type: 'text',
                text: `📎 ${fileName}`,
                marks: [
                  {
                    type: 'link',
                    attrs: {
                      href: objectUrl,
                      fileName,
                      blobUrl: objectUrl,
                    },
                  },
                ],
              },
            ],
          },
          { type: 'paragraph' },
        ])
        .run()
    },
    [allowAttachments, disabled, editor, rejectFileIfOversized],
  )

  const handlePickImageFile = useCallback(() => {
    if (disabled || !allowAttachments) return
    fileInputRef.current?.click()
  }, [allowAttachments, disabled])

  const handleImageFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      event.target.value = ''

      if (!file) return
      insertLocalImage(file)
    },
    [insertLocalImage],
  )

  const handlePickAttachmentFile = useCallback(() => {
    if (disabled || !allowAttachments) return
    attachmentInputRef.current?.click()
  }, [allowAttachments, disabled])

  const handleAttachmentFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      event.target.value = ''

      if (!file) return
      insertLocalFile(file)
    },
    [insertLocalFile],
  )

  const handlePaste = useCallback(
    (event: ClipboardEvent<HTMLDivElement>) => {
      if (!editor || disabled || !allowAttachments) return

      const items = event.clipboardData?.items
      if (!items) return

      let imageFile: File | null = null
      for (let index = 0; index < items.length; index += 1) {
        const item = items[index]
        if (item.type.startsWith('image/')) {
          imageFile = item.getAsFile()
          if (imageFile) break
        }
      }

      if (!imageFile?.size) return
      if (rejectFileIfOversized(imageFile)) return

      event.preventDefault()
      event.stopPropagation()
      insertLocalImage(imageFile)
    },
    [allowAttachments, disabled, editor, insertLocalImage, rejectFileIfOversized],
  )

  const clearPendingAttachmentFiles = useCallback(() => {
    pendingAttachmentFilesRef.current.forEach((_, blobUrl) => {
      if (blobUrlsRef.current.has(blobUrl)) {
        URL.revokeObjectURL(blobUrl)
        blobUrlsRef.current.delete(blobUrl)
      }
    })
    pendingAttachmentFilesRef.current.clear()
  }, [])

  const getPendingAttachmentFiles = useCallback((): TaskTrackerPendingAttachmentFile[] => {
    return Array.from(pendingAttachmentFilesRef.current.entries()).map(
      ([blobUrl, value]) => ({
        blobUrl,
        file: value.file,
        fileName: value.fileName,
      }),
    )
  }, [])

  return {
    fileInputRef,
    attachmentInputRef,
    handlePickImageFile,
    handleImageFileChange,
    handlePickAttachmentFile,
    handleAttachmentFileChange,
    handlePaste,
    getPendingAttachmentFiles,
    clearPendingAttachmentFiles,
  }
}

